// English (en) resource bundle for the patient portal.
// Keys are organized by surface (nav, common, page namespaces). Keep this file
// in sync with es.ts — every key here must have a Spanish counterpart.
const en = {
  common: {
    patient: 'Patient',
    careTeam: 'Care team',
    appointment: 'Appointment',
    document: 'Document',
    telemedicine: 'Telemedicine',
    virtualVisit: 'Virtual visit',
    loading: 'Loading...',
    na: 'N/A',
    notAssigned: 'Not assigned',
    refresh: 'Refresh',
    refreshing: 'Refreshing...',
    selfAccess: 'Self access',
    me: 'Me',
    total: 'total',
    active: 'active',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    reset: 'Reset',
    cancel: 'Cancel',
  },
  nav: {
    groups: {
      dashboard: 'Dashboard',
      medical: 'Medical',
      finance: 'Finance',
      understand: 'Understand',
    },
    items: {
      home: 'Home',
      appointments: 'Appointments',
      messages: 'Messages',
      contacts: 'Contacts',
      labs: 'Lab Results',
      records: 'Medical Records',
      prescriptions: 'Prescriptions',
      'care-plans': 'Care Plans',
      forms: 'Forms',
      billing: 'Billing',
      history: 'History',
      reports: 'Reports',
      webmd: 'WebMD',
      support: 'Support',
      settings: 'Settings',
      logout: 'Log Out',
    },
    brandSub: 'Patient Portal',
  },
  layout: {
    loadingPortal: 'Loading patient portal...',
    searchPlaceholder: 'Search appointments, messages, records, prescriptions...',
    activePatient: 'Active patient',
    mrn: 'MRN {{mrn}}',
    lastRefresh: 'Last refresh {{value}}',
    search: 'Search "{{query}}"',
  },
  home: {
    description: 'Unified access to appointments, forms, billing, lab reports, and care-team communication.',
    workspaceFallback: 'Patient workspace',
    metrics: {
      upcomingVisits: 'Upcoming Visits',
      formsPending: 'Forms Pending',
      openInvoices: 'Open Invoices',
      activeMeds: 'Active Meds',
      labReports: 'Lab Reports',
      outstanding: 'Outstanding',
    },
    formsToComplete_one: '{{count}} form to complete',
    formsToComplete_other: '{{count}} forms to complete',
    formsToCompleteBody: 'Complete your intake forms to prepare for your visit',
    docsNeedAttention_one: '{{count}} document needs attention',
    docsNeedAttention_other: '{{count}} documents need attention',
    docsNeedAttentionBody: 'Re-upload or correct the flagged documents',
    moreItems: '+{{count}} more',
    recentActivity: 'Recent Activity',
    loadingActivity: 'Loading activity...',
    noRecentActivity: 'No recent activity found.',
    upcomingSnapshot: 'Upcoming Appointments Snapshot',
    tableType: 'Type',
    tableDateTime: 'Date/Time',
    tableProvider: 'Provider',
    tableStatus: 'Status',
    noUpcoming: 'No upcoming appointments',
    loadError: 'Failed to load patient dashboard.',
  },
  appointments: {
    title: 'Appointments',
    description: 'Visit schedule, provider assignment, and telemedicine readiness.',
    loadError: 'Failed to load appointments.',
    bookNextTitle: 'Book your next visit',
    bookNextBody:
      'Request a visit that the clinic confirms from the EMR, or join a same-day waitlist when a doctor is accepting last-minute openings.',
    bookAppointment: 'Book an appointment',
    joinWaitlist: 'Join last-minute waitlist',
    searchPlaceholder: 'Search by type, provider, or telemedicine',
    status: {
      all: 'All statuses',
      scheduled: 'Scheduled',
      portalBooked: 'Portal booked',
      checkedIn: 'Checked in',
      completed: 'Completed',
      cancelled: 'Cancelled',
      noShow: 'No show',
      waiting: 'Waiting',
      seeDoctorSoon: 'See doctor soon',
    },
    upcomingRecent: 'Upcoming & recent visits',
    selectToSeeDetails: 'Select an appointment to see details and actions.',
    loading: 'Loading appointments...',
    none: 'No appointments found.',
    bookedInPortal: 'Booked in portal',
    bookedByStaff: 'Booked by staff',
    detailEyebrow: 'Appointment details',
    selectToView: 'Select an appointment to view details.',
    provider: 'Provider',
    portalBookedNote:
      'This appointment has been requested from the portal and is still awaiting confirmation on the EMR side.',
    telemedReadiness: 'Telemedicine readiness',
    joinLinkReady: 'Join link is ready.',
    joinLinkPending: 'Join link will appear once the session is prepared.',
    waitlistsEyebrow: 'Last-minute waitlists',
    waitlistsSub: 'Your current portal waitlist entries and queue status.',
    earlierSlot: 'Earlier slot available',
    expires: 'Expires {{value}}',
    accept: 'Accept',
    accepting: 'Accepting...',
    noWaitlists: 'You are not on any doctor waitlists right now.',
    queue: 'Queue #{{number}}',
    rank: 'Rank {{rank}}',
    beReadyBy: 'Be ready by {{value}}',
    actions: 'Actions',
    joinVirtual: 'Join virtual visit',
    messageCareTeam: 'Message care team',
    reschedule: 'Reschedule appointment',
    cancel: 'Cancel appointment',
    cancelling: 'Cancelling...',
    cancelConfirm:
      'Cancel this appointment? Your care team will be notified, and you may need to request a new time.',
    cancelError: 'Unable to cancel appointment.',
    portalLabel: 'Portal',
    staffLabel: 'Staff',
    // Date / location formatter fallbacks.
    timeTbd: 'Time TBD',
    tbd: 'TBD',
    virtualVisit: 'Virtual visit',
    locationTbd: 'Location TBD',
    // Status fallback for unmapped codes shown in the queue pill, etc.
    slots: '{{count}} slots',
    durationMin: '{{count}} min',
    // Confirmations & errors for booking/waitlist flows.
    waitlistJoined: "You joined {{provider}}'s last-minute waitlist for {{date}}.",
    waitlistJoinError: 'Unable to join the waitlist.',
    offerAcceptedConfirm: 'Earlier appointment accepted for {{date}}.',
    offerAcceptError: 'Unable to accept this waitlist offer.',
    rescheduledConfirm: 'Appointment rescheduled to {{date}}.',
    instantConfirmed: 'Instant appointment confirmed for {{date}}.',
    requestSentConfirm: 'Appointment request sent for {{date}}. A confirmation email is on its way.',
    bookingError: 'Unable to confirm this booking.',
    loadProvidersError: 'Unable to load available doctors for that day.',
    loadProviderDayError: 'Unable to load portal-bookable times for this doctor.',
    loadWaitlistError: 'Unable to load waitlist doctors for that day.',
    booking: {
      instantBadge: 'Instant booking',
      requestBadge: 'Appointment request',
      rescheduleTitle: 'Reschedule appointment',
      instantTitle: 'Book an instant appointment',
      requestTitle: 'Book an appointment',
      instantSubtitle: 'Choose an open portal slot and confirm it immediately.',
      requestSubtitle:
        'Pick a day, choose a working doctor, then send the request to the EMR for confirmation.',
      step1: '1. Select a date',
      step1Hint: 'Available doctors update for the selected day.',
      step2: '2. Select a doctor',
      loadingDoctors: 'Loading working doctors...',
      noDoctors: 'No doctors have portal-bookable time on that date.',
      step3: '3. Select a time',
      loadingTimes: 'Loading open times...',
      chooseDoctorFirst: 'Choose a doctor first.',
      noTimes: 'No portal-bookable times are open for this doctor on that day.',
      step4: '4. Select an appointment type',
      chooseTimeFirst: 'Choose a time to see the portal-visible appointment types for that slot.',
      step5: '5. Confirm details',
      doctor: 'Doctor',
      selectDoctor: 'Select a doctor',
      time: 'Time',
      selectTime: 'Select a time',
      appointmentType: 'Appointment type',
      selectAppointmentType: 'Select an appointment type',
      visitEnd: 'Visit end',
      rescheduleNote: 'This will replace your current appointment with the newly selected time.',
      instantNote: 'Instant booking confirms the selected portal slot immediately.',
      requestNote:
        'This request will enter the EMR as Portal booked and wait for clinic confirmation. An email summary will be sent after submission.',
      confirming: 'Confirming...',
      confirmReschedule: 'Confirm reschedule',
      confirmInstant: 'Confirm instant appointment',
      sendRequest: 'Send appointment request',
    },
    waitlistDialog: {
      badge: 'Last-minute waitlist',
      title: 'Join a doctor waitlist',
      subtitle:
        'See which doctors have waitlist enabled for the selected day, join the queue, and watch your rank from the portal.',
      selectDay: 'Select a day',
      selectDayHint: 'Only doctors with an active waitlist for that day will appear here.',
      loadingDoctors: 'Loading waitlist doctors...',
      noDoctors: 'No doctors have an active waitlist for that day.',
      cutoff: 'Cutoff {{value}}',
      joinedCount: '{{count}}/{{max}} joined',
      alreadyJoinedTitle: 'You already joined this queue.',
      alreadyJoinedDetail: 'Queue #{{number}}',
      rankSuffix: ' • Rank {{rank}}',
      readyBySuffix: ' • Be ready by {{value}}',
      notePlaceholder: 'Optional note for the clinic',
      alreadyJoined: 'Already joined',
      waitlistFull: 'Waitlist full',
      join: 'Join waitlist',
    },
  },
  billing: {
    title: 'Billing',
    description: 'Invoices, due dates, posted payments, and remaining balances.',
    loadError: 'Failed to load billing.',
    invoice: 'Invoice',
    date: 'Date',
    dueDate: 'Due Date',
    balance: 'Balance',
    total: 'Total',
    status: 'Status',
    payNow: 'Pay now',
    starting: 'Starting…',
    noEntries: 'No billing entries',
    checkoutError: 'Online payment is not available right now.',
    checkoutMissingUrl: 'Could not start the payment. Please try again.',
  },
  records: {
    title: 'Medical Records',
    description: 'Upload supporting documents and track review status from your care team.',
    loadError: 'Failed to load medical records.',
    uploadDocument: 'Upload Document',
    noDocumentTypes:
      'Your care team has not configured document types yet. Please contact your provider to enable document uploads.',
    documentType: 'Document Type',
    file: 'File',
    fileHint: 'Allowed: PDF, JPG, PNG, HEIC. Maximum size: 25 MB.',
    titleLabel: 'Title',
    titlePlaceholder: 'e.g. Insurance card front',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Optional notes for staff reviewing this file',
    sizeLabel: 'Size: {{value}}',
    mimeLabel: 'MIME type: {{value}}',
    unknown: 'Unknown',
    uploading: 'Uploading...',
    selectTypeAndFile: 'Select a document type and file before uploading.',
    uploadSuccess: 'Document uploaded successfully.',
    uploadError: 'Failed to upload document.',
    library: 'Document Library',
    loadingDocuments: 'Loading documents...',
    libraryEmpty:
      'No records available yet. Upload a referral, insurance card, or outside report to get started.',
    uploaded: 'Uploaded:',
    reviewed: 'Reviewed:',
    fileSize: 'File size:',
    source: 'Source:',
    reviewNote: 'Review note:',
  },
  forms: {
    title: 'Forms',
    description: 'Complete intake, consent, and follow-up questionnaires directly in the portal.',
    loadError: 'Failed to load forms.',
    pending: 'Pending ({{count}})',
    completed: 'Completed ({{count}})',
    completedForms: 'Completed Forms',
    pendingForms: 'Pending Forms',
    loading: 'Loading forms...',
    noCompleted: 'No completed forms yet.',
    noPending: 'No pending forms assigned.',
    formFallback: 'Form',
    generalCategory: 'General',
    completion: 'Completion',
    due: 'Due',
    review: 'Review',
    detail: 'Form Detail',
    loadingForm: 'Loading form...',
    selectForm: 'Select a form to view its questions and complete it here.',
    summary: 'Form Summary',
    status: 'Status',
    completed_label: 'Completed:',
    reviewed: 'Reviewed:',
    signatureRequired: 'Signature required:',
    estimatedTime: 'Estimated time:',
    minutes: '{{count}} min',
    publicLinkFallback: 'Public-link fallback is available for this form.',
    openExternal: 'Open external view',
    definitionUnavailable: 'This form definition is not available for in-portal rendering.',
    signatureNote:
      'Typing your legal name into the signature field counts as patient signature for submission.',
    saveProgressNote: 'You can save progress and return later.',
    reset: 'Reset',
    changesReset: 'Changes reset to last saved state.',
    saveDraft: 'Save Draft',
    saving: 'Saving...',
    draftSaved: 'Draft saved.',
    saveDraftError: 'Failed to save draft.',
    submit: 'Submit Form',
    submitting: 'Submitting...',
    submitSuccess: 'Form submitted successfully.',
    submitError: 'Failed to submit form.',
    detailLoadError: 'Failed to load form detail.',
  },
  settings: {
    title: 'Settings',
    description: 'Portal preferences, context visibility, and account-level information.',
    account: 'Account',
    displayName: 'Display Name:',
    email: 'Email:',
    defaultProvider: 'Default Provider:',
    contextAccess: 'Context Access',
    contextId: 'Context ID:',
    language: 'Language',
    languageHint: 'Choose the language used across the patient portal.',
    languageEn: 'English',
    languageEs: 'Spanish',
    security: 'Security',
    changePassword: 'Change password',
  },
  changePassword: {
    title: 'Update your password',
    subtitle: 'For your security, you must set a new password before continuing.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    policyHint:
      'Use at least 12 characters with an uppercase letter, a lowercase letter, a number, and a symbol.',
    submit: 'Update password',
    submitting: 'Updating...',
    mismatch: 'New password and confirmation do not match.',
    sameAsCurrent: 'New password must be different from your current password.',
    policyError:
      'Password must be at least 12 characters and include uppercase, lowercase, a number, and a symbol.',
    fillAll: 'Enter your current password and type the new password twice.',
    genericError: 'Unable to update password.',
    success: 'Password updated. Please sign in again with your new password.',
    signOut: 'Sign out',
  },
  messages: {
    title: 'Messages',
    description: 'Review secure conversations with your clinical and billing teams.',
    notEnabled: 'This patient context does not have secure messaging enabled.',
    sidebarTitle: 'Messages',
    sidebarSubtitle: 'Secure communication for the active patient context.',
    searchPlaceholder: 'Search subjects or messages...',
    filterAll: 'All',
    filterOpen: 'Open',
    filterClosed: 'Closed',
    subjectPlaceholder: 'Subject',
    messagePlaceholder: 'Write your message...',
    newConversation: 'New conversation',
    creating: 'Creating...',
    loadingConversations: 'Loading conversations...',
    noMatches: 'No conversations match your current filter.',
    careTeam: 'Care team',
    noMessagesYet: 'No messages yet.',
    you: 'You',
    replyPlaceholder: 'Write a secure reply...',
    send: 'Send',
    sending: 'Sending...',
    loadingMessages: 'Loading messages...',
    selectConversation: 'Select a conversation to start messaging.',
    loadError: 'Failed to load messages.',
    detailLoadError: 'Failed to load conversation.',
    sendError: 'Failed to send message.',
    createError: 'Failed to create conversation.',
  },
  contacts: {
    title: 'Contacts and Profile',
    description: 'Primary patient demographics and assigned care-team directory.',
    assignedProvider: 'Assigned provider',
    phoneViaMessages: 'Use secure messages',
    emailViaMessages: 'Contact via portal messages',
    patientProfile: 'Patient Profile',
    name: 'Name:',
    mrn: 'MRN:',
    dob: 'Date of Birth:',
    email: 'Email:',
    phone: 'Phone:',
    status: 'Status:',
    statusActive: 'Active',
    provider: 'Provider:',
    headerName: 'Name',
    headerRole: 'Role',
    headerPhone: 'Phone',
    headerEmail: 'Email',
    noContacts: 'No care-team contacts available',
  },
  history: {
    title: 'History',
    description: 'Chronological stream across visits, labs, billing, and messages.',
    headerType: 'Type',
    headerTitle: 'Title',
    headerDateTime: 'Date/Time',
    headerDetail: 'Detail',
    headerStatus: 'Status',
    empty: 'No timeline events found',
    loadError: 'Failed to load timeline.',
  },
  reports: {
    title: 'Reports',
    description: 'Financial snapshot and quick account-level reporting metrics.',
    totalInvoicedYtd: 'Total Invoiced YTD',
    totalPaidYtd: 'Total Paid YTD',
    outstandingBalance: 'Outstanding Balance',
    reportNotes: 'Report Notes',
    patient: 'Patient:',
    invoicesThisYear: 'Invoices this year:',
    outstanding: 'Outstanding balance:',
    loadError: 'Failed to load reports.',
  },
  support: {
    title: 'Support',
    description: 'Contact details for technical, billing, and urgent care coordination.',
    portalSupport: 'Portal Support',
    portalSupportLine: 'Use secure messages from this portal',
    portalSupportBody:
      'Account access, login, and document upload issues should be sent from the Messages page.',
    billingSupport: 'Billing Support',
    yourCareTeam: 'Your care team',
    billingSupportBody:
      'Invoices, payment posting, and insurance questions can be routed through your assigned care team.',
    urgentCare: 'Urgent Care Guidance',
    urgentCareLine: 'Call clinic directly',
    urgentCareBody: 'Portal messages are not monitored for emergencies.',
  },
  labResults: {
    title: 'Lab Results',
    description: 'Recent laboratory activity, report statuses, and flagged values.',
    headerPanel: 'Panel',
    headerCollected: 'Collected',
    headerOrderingProvider: 'Ordering Provider',
    headerAbnormal: 'Abnormal',
    headerStatus: 'Status',
    seeCareTeamNotes: 'See care team notes',
    empty: 'No lab reports available',
    loadError: 'Failed to load lab results.',
  },
  carePlans: {
    title: 'Care Plans',
    description: 'Plan milestones, progress score, and next clinical review date.',
    empty: 'No care plans assigned.',
    carePlanFallback: 'Care plan',
    progress: 'Progress',
    nextReview: 'Next review',
    loadError: 'Failed to load care plans.',
  },
  prescriptions: {
    title: 'Prescriptions',
    description: 'Active medications, dosage schedule, and refill availability.',
    headerMedication: 'Medication',
    headerDosage: 'Dosage',
    headerFrequency: 'Frequency',
    headerRefills: 'Refills',
    headerPharmacy: 'Pharmacy',
    headerStatus: 'Status',
    headerAction: 'Action',
    prescribedBy: 'Prescribed by {{name}}',
    noPharmacy: 'No pharmacy assigned',
    requestRefill: 'Request refill',
    requesting: 'Requesting...',
    pharmacyRequired: 'A pharmacy must be assigned before a refill can be requested.',
    latestRequest: 'Latest request: {{status}} on {{date}}',
    noRequestsYet: 'No refill requests yet',
    empty: 'No prescriptions available',
    refillFormTitle: 'Refill request for {{drug}}',
    requestedRefills: 'Requested refills',
    currentPrescription: 'Current prescription',
    medicationDetailsPending: 'Medication details pending',
    scheduleNotPosted: 'Schedule not posted',
    pharmacyNote: 'Pharmacy note',
    pharmacyNotePlaceholder:
      'Add context for your care team or pharmacy, such as travel timing or supply issues.',
    submitRequest: 'Submit refill request',
    submitting: 'Submitting...',
    assignedPharmacy: 'Assigned pharmacy',
    phoneUnavailable: 'Phone number not available',
    recentRequests: 'Recent refill requests',
    requestedRefillsCount: 'Requested refills: {{count}}',
    noRequestsForPrescription: 'No refill requests have been submitted for this prescription yet.',
    recentActivity: 'Recent refill activity',
    requestedViaPharmacy: 'Requested {{count}} refill(s) via {{pharmacy}}',
    noActivity: 'No refill requests submitted yet.',
    loadError: 'Failed to load prescriptions.',
    minRefillsError: 'Requested refills must be at least 1.',
    submitSuccess: 'Refill request submitted. Your care team will review it.',
    submitError: 'Unable to submit refill request.',
  },
  webmd: {
    title: 'WebMD Resources',
    description: 'Trusted educational references relevant to your current care plan.',
    suggestedReading: 'Suggested Reading',
    empty: 'No patient-specific education topics are available yet.',
  },
};

export default en;
export type PortalTranslations = typeof en;
